// import components
import { IconButton } from '@mui/material';
import { Tooltip } from 'components/shared/index';
import { StyledButtonBoxWrapper } from 'components/shared/TenantFooterBar/TenantFooterBar.styles';
import SpeechBubble from 'components/shared/FeatureDiscovery/components/SpeechBubble';

// import styles
import {
  StyledNewFeatureFab,
  StyledNewFeatureFontAwesomeSvgIcon
} from 'components/shared/FeatureDiscovery/FeatureTour.styles';
import { StyledSmallerIcon } from 'components/shared/Icon/Icon.styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { FloatingSpeechBubbleButtonProps } from './FloatingSpeechBubbleButton.types';

export const FloatingSpeechBubbleButton = ({
  speechBubbleText,
  tooltipTitle,
  uniqueFeatureName,
  onClick
}: FloatingSpeechBubbleButtonProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'featureTour'
  });
  const [isFabClosing, setIsFabClosing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isNewFeatureInformationHidden, setIsNewFeatureInformationHidden] =
    useSessionStorage<boolean>(uniqueFeatureName, false);
  const [showAnimatedSpeechBubble, setShowAnimatedSpeechBubble] =
    useState(false);

  const handleCloseIcon = (event) => {
    event.stopPropagation();
    setIsFabClosing(true);
    setTimeout(() => {
      setIsNewFeatureInformationHidden(true);
    }, 600);
  };

  // listen for storage changes and update state
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === uniqueFeatureName) {
        const newValue = event.newValue === 'true';
        setIsNewFeatureInformationHidden(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const showTimer = setTimeout(() => setShowAnimatedSpeechBubble(true), 1500); // show after 1s
    const hideTimer = setTimeout(
      () => setShowAnimatedSpeechBubble(false),
      8000
    ); // hide after 8s more

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  if (isNewFeatureInformationHidden) {
    return null;
  }

  return (
    <>
      <SpeechBubble
        showAnimatedSpeechBubble={showAnimatedSpeechBubble}
        speechBubbleText={speechBubbleText}
      />

      <Tooltip title={tooltipTitle} placement="left">
        <StyledNewFeatureFab
          color="primary"
          aria-label={t('information')}
          onClick={onClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          bounceOut={isFabClosing}
        >
          <StyledNewFeatureFontAwesomeSvgIcon
            type="hand-wave"
            color="inherit"
            fontSize="medium"
          />

          <StyledButtonBoxWrapper isHovered={isHovered}>
            <Tooltip title={`${t('hide')}`} arrow>
              <IconButton
                size="small"
                onClick={handleCloseIcon}
                component="span"
              >
                <StyledSmallerIcon type="xmark" color="inherit" />
              </IconButton>
            </Tooltip>
          </StyledButtonBoxWrapper>
        </StyledNewFeatureFab>
      </Tooltip>
    </>
  );
};
